import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ContainerVisualizer from '../components/ContainerVisualizer'
import ThrillFillSpillGuide from '../components/ThrillFillSpillGuide'
import { useGarden } from '../context/GardenContext'
import { usePlantCatalog } from '../context/PlantCatalogContext'
import { CONTAINER_SIZE_INFO, type Container } from '../types'

const CROWDING_GUIDANCE: Record<string, number> = { small: 1, medium: 3, large: 5, xlarge: 7 }

function getMismatchWarnings(container: Container, getPlantById: ReturnType<typeof usePlantCatalog>['getPlantById']): string[] {
  const plants = container.plantIds.map(id => getPlantById(id)).filter(Boolean)
  const warnings: string[] = []

  if (plants.length >= (CROWDING_GUIDANCE[container.size] ?? 3)) {
    warnings.push('Crowding risk: this container is at or above the rough guidance for its size. Check each plant card for depth and spacing notes.')
  }

  const waterNeeds = plants.map(plant => plant!.water)
  if (waterNeeds.includes('low') && waterNeeds.includes('high')) {
    warnings.push('Water mismatch: drought-tolerant and moisture-loving plants may struggle in the same pot.')
  }

  if (plants.some(plant => !plant!.sunlight.includes(container.sunExposure))) {
    warnings.push('Sun check: at least one plant may prefer different light than this container receives.')
  }

  const standalonePlants = plants.filter(plant => plant!.containerSuitability === 'not_recommended')
  if (standalonePlants.length > 0) {
    warnings.push(`${standalonePlants.map(plant => plant!.name).join(', ')} may not be a good container candidate based on current source review.`)
  }

  if (container.location === 'balcony') {
    const lowWind = plants.filter(plant => plant!.windTolerance === 'low')
    if (lowWind.length > 0) {
      warnings.push(`${lowWind.map(plant => plant!.name).join(', ')} may need a sheltered balcony spot.`)
    }
  }

  return warnings
}

export default function ContainerDesign() {
  const { state, dispatch } = useGarden()
  const { filterPlants, getPlantById } = usePlantCatalog()
  const navigate = useNavigate()
  const [selectedContainer, setSelectedContainer] = useState<string | null>(state.containers[0]?.id ?? null)
  const [searchText, setSearchText] = useState('')

  const container = state.containers.find(c => c.id === selectedContainer)
  const suggestedPlants = container
    ? filterPlants(null, {
        region: state.region,
        sunlight: [container.sunExposure],
        searchText,
      }).filter(plant => !container.plantIds.includes(plant.id))
    : []

  function addPlant(plantId: string) {
    if (!container) return
    dispatch({ type: 'ADD_PLANT_TO_CONTAINER', containerId: container.id, plantId })
  }

  function removePlant(plantId: string) {
    if (!container) return
    dispatch({ type: 'REMOVE_PLANT_FROM_CONTAINER', containerId: container.id, plantId })
  }

  if (state.containers.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center space-y-4 page-enter">
        <h1 className="font-pixel text-ac-brown text-base">No containers yet</h1>
        <p className="font-body text-ac-brown-light">Set up your containers first, then come back to design them.</p>
        <button className="ac-btn-green" onClick={() => navigate('/setup')}>Set up containers</button>
      </div>
    )
  }

  const warnings = container ? getMismatchWarnings(container, getPlantById) : []
  const guidanceLimit = container ? CROWDING_GUIDANCE[container.size] ?? 3 : 3
  const meterValue = container ? Math.min(100, (container.plantIds.length / guidanceLimit) * 100) : 0

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 page-enter">
      <div className="text-center mb-6 space-y-2">
        <h1 className="font-pixel text-ac-brown text-base">Design Containers</h1>
        <ThrillFillSpillGuide />
      </div>

      <div className="flex gap-6 items-start flex-col lg:flex-row">
        <div className="lg:w-80 w-full space-y-4">
          <div className="flex flex-wrap gap-2">
            {state.containers.map(item => (
              <button
                key={item.id}
                onClick={() => setSelectedContainer(item.id)}
                className={`filter-chip ${selectedContainer === item.id ? 'active' : ''}`}
              >
                {item.nickname}
              </button>
            ))}
            <button className="filter-chip" onClick={() => navigate('/setup')}>Add</button>
          </div>

          {container && (
            <div className="ac-card flex flex-col items-center gap-4">
              <ContainerVisualizer container={container} onRemovePlant={removePlant} />

              <div className="w-full">
                <div className="flex justify-between font-body text-xs text-ac-brown-light mb-1">
                  <span>{container.plantIds.length} plants · crowding guidance {guidanceLimit}</span>
                  <span>{CONTAINER_SIZE_INFO[container.size].label}</span>
                </div>
                <div className="h-2 bg-ac-bg rounded-full overflow-hidden border border-ac-brown">
                  <div className="h-full bg-ac-green rounded-full transition-all" style={{ width: `${meterValue}%` }} />
                </div>
              </div>

              {warnings.length > 0 && (
                <div className="w-full space-y-2">
                  {warnings.map((warning, index) => (
                    <p key={index} className="font-body text-xs bg-ac-yellow/35 text-ac-brown rounded-lg px-3 py-2 border border-ac-brown">
                      {warning}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-3">
            <input
              className="ac-input flex-1"
              placeholder="Search plants to add..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            <button className="ac-btn-outline text-sm whitespace-nowrap" onClick={() => navigate('/browse')}>
              Full library
            </button>
          </div>

          {container && (
            <p className="font-body text-xs text-ac-brown-light">
              Showing plants for <strong>{container.sunExposure.replace('-', ' ')}</strong>
              {state.region ? ` with ${state.region} native options prioritized` : ''}.
            </p>
          )}

          {suggestedPlants.length === 0 && (
            <div className="text-center py-8 space-y-2">
              <p className="font-body text-ac-brown-light text-sm">
                {searchText ? 'No plants match your search.' : 'All matching plants are already in this container.'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:max-h-[600px] lg:overflow-y-auto ac-scroll pr-1">
            {suggestedPlants.map(plant => (
              <button
                key={plant.id}
                onClick={() => addPlant(plant.id)}
                className="text-left p-3 rounded-xl border-2 border-ac-brown bg-ac-cream font-body transition-all hover:bg-ac-bg hover:shadow-ac-sm"
                style={{ borderLeftColor: plant.accentColor, borderLeftWidth: 6 }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{plant.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-ac-brown text-sm truncate">{plant.name}</p>
                    <p className="text-xs text-ac-brown-light capitalize">{plant.role.join(' / ')}</p>
                  </div>
                  <span className="text-ac-green text-lg font-bold shrink-0">+</span>
                </div>
                <div className="flex gap-2 flex-wrap text-xs text-ac-brown-light">
                  <span>{plant.water} water</span>
                  <span className="capitalize">{plant.careLevel} care</span>
                  {plant.toxicity.pets && <span className="text-red-700">pets warning</span>}
                  {state.region && plant.nativeRegions.includes(state.region) && <span className="text-ac-green-dark font-bold">{state.region} native</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button className="ac-btn-green" onClick={() => navigate('/lists')}>
          Review list
        </button>
      </div>
    </div>
  )
}
